import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { InfoApi } from "../../api/InfoApi";
import { OrgAdminApi } from "../../api/OrgAdminApi";
import BackgroundSelection from "../../components/leaderboardSetting/BackgroundSelection";
import GroupBySelection from "../../components/leaderboardSetting/GroupBySelection";
import ItemSelection from "../../components/leaderboardSetting/ItemSelection";
import LeaderboardPreviewDialog from "../../components/leaderboardSetting/LeaderboardPreviewDialog";
import ScopeSelection from "../../components/leaderboardSetting/ScopeSelection";
import { buildLeaderboard } from "../../helper/buildLeaderboard";
import { openLeaderboardDisplay } from "../../helper/leaderboardSlide";
import { confirmDeleteLeaderboardSetting } from "./UserLeaderboardSettingListPage";

const LIST_PATH = "/user/leaderboardSettings";

const DISPLAY_RULES = [
  "最多顯示前 30 名",
  "同分同名次，例如 1、2、2、4",
  "第 1 名顯示「榜首」",
  "你的一行會被標示",
];

function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

function cardSx() {
  return {
    p: 2.5,
    borderRadius: 3,
    border: "1px solid #eef0f3",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  };
}

function parseLeaderboardCode(code) {
  const parts = String(code || "").split("/");
  if (parts.length < 5 || parts.some((part) => part.trim() === "")) {
    return null;
  }
  return {
    scope: parts[0],
    scopeArg: parts[1],
    groupBy: parts[2],
    item: parts[3],
    skillCode: parts[4],
    suffix: parts.length > 5 ? parts[5] : "",
  };
}

function flagOn(value) {
  return Number(value) === 1;
}

export default function UserLeaderboardSettingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [scope, setScope] = useState("organization");
  const [scopeArg, setScopeArg] = useState("_");
  const [groupBy, setGroupBy] = useState("member");
  const [item, setItem] = useState("duration");
  const [skillCode, setSkillCode] = useState("all");
  const [suffix, setSuffix] = useState("");
  const [title, setTitle] = useState("");
  const [displayPos, setDisplayPos] = useState(0);
  const [enable, setEnable] = useState(false);
  const [displaySlideshow, setDisplaySlideshow] = useState(false);
  const [background, setBackground] = useState("yellow");
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const settingId = Number(id);
    if (!Number.isInteger(settingId) || settingId <= 0) {
      setIsLoading(false);
      setLoadError("找不到排行榜設定");
      return;
    }

    setIsLoading(true);
    setLoadError("");
    OrgAdminApi.getLeaderboardSettingCallback(settingId, (isSuccess, msg, data) => {
      setIsLoading(false);
      if (!isSuccess || !data) {
        setRecord(null);
        setLoadError(typeof msg === "string" && msg ? msg : "找不到排行榜設定");
        return;
      }

      const parsed = parseLeaderboardCode(data.leaderboard_code);
      if (!parsed) {
        setRecord(null);
        setLoadError("排行榜代碼不正確");
        return;
      }

      console.log("DEBUG: loaded data", data);
      setRecord(data);
      setScope(parsed.scope);
      setScopeArg(parsed.scopeArg);
      setGroupBy(parsed.groupBy);
      setItem(parsed.item);
      setSuffix(parsed.suffix);
      setSkillCode(parsed.skillCode);
      setTitle(data.title || "");
      setDisplayPos(data.display_pos ?? 0);
      setDisplaySlideshow(flagOn(data.display_slideshow));
      setBackground(data.background || "yellow");
    });
  }, [id]);

  useEffect(() => {
    OrgAdminApi.getMyGrpListCallback((isSuccess, msg, data) => {
      setGroupsLoading(false);
      if (!isSuccess) {
        toast.error(typeof msg === "string" && msg ? msg : "讀取社列表失敗");
        setGroups([]);
        return;
      }
      setGroups(asList(data));
    });
  }, []);

  useEffect(() => {
    let active = true;
    InfoApi.getSkillList()
      .then((data) => {
        if (!active) {
          return;
        }
        const list = asList(data)
          .filter((skill) => String(skill?.code || "").trim() !== "")
          .sort((left, right) => {
            const position = Number(left.dashboard_position || 0) - Number(right.dashboard_position || 0);
            if (position !== 0) {
              return position;
            }
            return String(left.name_zh || left.code).localeCompare(String(right.name_zh || right.code), "zh");
          });
        setSkills(list);
        setSkillsLoading(false);
      })
      .catch(() => {
        if (!active) {
          return;
        }
        toast.error("讀取活動失敗");
        setSkills([]);
        setSkillsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const selectedSkill = skills.find((skill) => skill.code === skillCode);
  const built = useMemo(
    () =>
      buildLeaderboard({
        scope,
        scopeArg,
        groupBy,
        title,
        item,
        skillCode,
        skillName: selectedSkill?.name_zh || selectedSkill?.name_en || "",
        leaderboardCode: record?.leaderboard_code,
        suffix: suffix,
      }),
    [scope, scopeArg, groupBy, item, title, skillCode, selectedSkill]
  );

  const applyRecord = (data) => {
    const parsed = parseLeaderboardCode(data?.leaderboard_code);
    setRecord(data);
    if (!parsed) {
      return;
    }
    setScope(parsed.scope);
    setScopeArg(parsed.scopeArg);
    setGroupBy(parsed.groupBy);
    setItem(parsed.item);
    setSkillCode(parsed.skillCode);
    setTitle(data.title || "");
    setDisplayPos(data.display_pos ?? 0);
    setDisplaySlideshow(flagOn(data.display_slideshow));
    setBackground(data.background || "yellow");
  };

  const handleUpdate = () => {
    if (!record?.id || submitting) {
      return;
    }

    const trimmedTitle = title.trim();
    const positionText = String(displayPos).trim();
    const position = Number(positionText);
    if (!trimmedTitle) {
      toast.error("請輸入標題");
      return;
    }
    if (positionText === "" || !Number.isInteger(position)) {
      toast.error("請輸入顯示順序");
      return;
    }
    if (!built.ready) {
      toast.error("請完成排行榜選擇");
      return;
    }

    setSubmitting(true);
    OrgAdminApi.updateLeaderboardSettingCallback(
      record.id,
      {
        leaderboard_code: built.code, // ken: do not update leaderboard_code
        title: trimmedTitle,
        display_pos: position,
        enable: enable ? 1 : 0,
        display_slideshow: displaySlideshow ? 1 : 0,
        background,
      },
      (isSuccess, msg, data) => {
        setSubmitting(false);
        if (!isSuccess || !data) {
          toast.error(typeof msg === "string" && msg ? msg : "更新失敗");
          return;
        }

        applyRecord(data);
        toast.success("更新成功");
      }
    );
  };

  const handleDelete = () => {
    if (!record?.id) {
      return;
    }

    confirmDeleteLeaderboardSetting(record.id, () => {
      navigate(LIST_PATH);
    });
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", py: 1 }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827" }}>
          編輯排行榜
        </Typography>
        <Box
          sx={{
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
            bgcolor: "#e8f1ff",
            color: "#1d4ed8",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          本學年
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          disabled={!record || built.code.includes("?")}
          onClick={() => openLeaderboardDisplay(built.code, background)}
        >
          預覽
        </Button>
        <Button
          variant="outlined"
          disabled={!record || built.code.includes("?")}
          onClick={() => setPreviewOpen(true)}
        >
          查看數據
        </Button>
        <Button variant="text" onClick={() => navigate(LIST_PATH)}>
          返回列表
        </Button>
      </Stack>

      {loadError ? (
        <Paper elevation={0} sx={cardSx()}>
          <Stack spacing={2} alignItems="flex-start">
            <Typography>{loadError}</Typography>
            <Button variant="outlined" onClick={() => navigate(LIST_PATH)}>
              返回列表
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {record ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.7fr) minmax(280px, 0.9fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Stack spacing={2}>
          <Paper elevation={0} sx={{ ...cardSx(), px: 3, py: 3.5 }}>
            <ScopeSelection
              scope={scope}
              scopeArg={scopeArg}
              groups={groups}
              groupsLoading={groupsLoading}
              readOnly
              onChange={({ scope: nextScope, scopeArg: nextScopeArg }) => {
                setScope(nextScope);
                setScopeArg(nextScopeArg);
                // console.log("DEBUG: onChange", nextScope, nextScopeArg);
              }}
            />
            <GroupBySelection groupBy={groupBy} onChange={setGroupBy} />
            <ItemSelection
              item={item}
              skillCode={skillCode}
              skills={skills}
              skillsLoading={skillsLoading}
              onChange={({ item: nextItem, skillCode: nextSkillCode }) => {
                setItem(nextItem);
                setSkillCode(nextSkillCode);
              }}
            />
          </Paper>
          <Paper elevation={0} sx={cardSx()}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1.5 }}>
                排行榜標題建議
              </Typography>
              {/* <pre> DEBUG: 
                scopeArg: {scopeArg} <br />
                built: {JSON.stringify(built, null, 2)} <br />
                suffix: {suffix} <br />
                record: {JSON.stringify(record, null, 2)}<br />
              </pre> */}
              <Typography sx={{ fontWeight: 800, color: "#111827", fontSize: 18 }}>
                {built.headline} {built.metricLine}
              </Typography>
              
            </Paper>
          </Stack>

          <Stack spacing={2}>
            

            <Paper elevation={0} sx={cardSx()}>
              <Stack spacing={2}>
                <TextField
                  id="title"
                  name="title"
                  label="標題"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  fullWidth
                />
                <TextField
                  id="display_pos"
                  name="display_pos"
                  label="顯示順序"
                  type="number"
                  value={displayPos}
                  onChange={(event) => setDisplayPos(event.target.value)}
                  fullWidth
                />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={cardSx()}>
              <BackgroundSelection value={background} onChange={setBackground} />
            </Paper>

            <Paper elevation={0} sx={cardSx()}>
              <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
                排行榜代碼
              </Typography>
              <Box
                sx={{
                  bgcolor: "#f3f4f6",
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  color: "#6b7280",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  wordBreak: "break-all",
                }}
              >
                {built.code} 
              </Box>
            </Paper>

            {/* <Paper elevation={0} sx={cardSx()}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>顯示方式</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#2563eb" }}>
                {DISPLAY_RULES.map((rule) => (
                  <Typography
                    key={rule}
                    component="li"
                    variant="body2"
                    sx={{ color: "#1f2937", mb: 0.75, "&::marker": { color: "#2563eb" } }}
                  >
                    {rule}
                  </Typography>
                ))}
              </Box>
            </Paper> */}

            <FormControlLabel
              control={
                <Switch
                  checked={displaySlideshow}
                  onChange={(event) => setDisplaySlideshow(event.target.checked)}
                />
              }
              label="是否播放"
            />
            {/* <FormControlLabel
              control={
                <Switch checked={enable} onChange={(event) => setEnable(event.target.checked)} />
              }
              label="是否生效"
            /> */}

            <Button
              fullWidth
              disabled={!built.ready || submitting || title.trim() === ""}
              onClick={handleUpdate}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 800,
                fontSize: 16,
                boxShadow: "none",
                bgcolor: "#2563eb",
                color: "#fff",
                "&:hover": { bgcolor: "#1d4ed8", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" },
              }}
            >
              更新
            </Button>
            <Button variant="outlined" color="error" onClick={handleDelete}>
              刪除
            </Button>
          </Stack>
        </Box>
      ) : null}
      <LeaderboardPreviewDialog
        open={previewOpen}
        code={built.code}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
}
